import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import ApiInspector from '../../components/ApiInspector';
import { RowIcon } from '../../components/BopRowActions';
import PrintModal from '../../components/PrintModal';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { formatCurrency } from '../../utils/formatters';
import { API_DOCS } from './BilancioContabAnaliticaPage.api';
import './BilancioContabAnaliticaPage.css';

type Natura = 'COSTO' | 'RICAVO';

interface BilNode {
  id: number;
  codice: string;
  descrizione: string;
  livello: number;
  natura: Natura;
  attivo: boolean;
  divisioneId: number | null;
  consuntivo: number;
  budget: number;
  figli: BilNode[];
}

interface Totali {
  ricaviConsuntivo: number;
  ricaviBudget: number;
  costiConsuntivo: number;
  costiBudget: number;
  risultatoConsuntivo: number;
  risultatoBudget: number;
}

interface DivisioneBilancio {
  id: number | null;
  codice: string;
  descrizione: string;
  colore: string | null;
  attivo: boolean;
  alberoRicavi: BilNode[];
  alberoCosti: BilNode[];
  totali: Totali;
}

interface BilancioData {
  anno: number;
  dataDal: string;
  dataAl: string;
  periodDays: number;
  yearDays: number;
  divisioni: DivisioneBilancio[];
  alberoRicavi: BilNode[];
  alberoCosti: BilNode[];
  totali: Totali;
}

const LIVELLO_LABEL: Record<number, string> = { 1: 'Centro', 2: 'Conto', 3: 'Sottoconto' };

const annoCorrente = new Date().getFullYear();
const ANNI: number[] = [];
for (let a = annoCorrente + 1; a >= annoCorrente - 4; a--) ANNI.push(a);

const isZero = (v: number) => Math.abs(v) < 0.005;

const fmtData = (d: string) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtPerc = (consuntivo: number, budget: number): string => {
  if (isZero(budget)) return '—';
  const p = ((consuntivo - budget) / Math.abs(budget)) * 100;
  return `${p > 0 ? '+' : ''}${p.toFixed(1)}%`;
};

// favorevole: ricavi se consuntivo >= budget, costi se consuntivo <= budget
const classeScost = (consuntivo: number, budget: number, natura: Natura): string => {
  if (isZero(budget)) return '';
  const favorevole = natura === 'RICAVO' ? consuntivo >= budget : consuntivo <= budget;
  return favorevole ? 'bca-scost--ok' : 'bca-scost--ko';
};

type Vista = 'divisioni' | 'natura';

export default function BilancioContabAnaliticaPage() {
  const { societaId, setSocietaId, listaSocieta, isUnica } = useFiltroSocieta();

  const [anno, setAnno] = useState<number>(annoCorrente);
  const [dataDal, setDataDal] = useState<string>(`${annoCorrente}-01-01`);
  const [dataAl, setDataAl] = useState<string>(`${annoCorrente}-12-31`);
  const [data, setData] = useState<BilancioData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hideZero, setHideZero] = useState(true);
  const [vista, setVista] = useState<Vista>('divisioni');
  const [filtroDivisione, setFiltroDivisione] = useState<string>(''); // '' = tutte
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [expandedDivisioni, setExpandedDivisioni] = useState<Set<string>>(new Set());
  const [stampaOpen, setStampaOpen] = useState(false);

  const cambiaAnno = (nuovo: number) => {
    setAnno(nuovo);
    setDataDal(`${nuovo}-01-01`);
    setDataAl(`${nuovo}-12-31`);
  };

  const collectExpandable = (nodes: BilNode[], acc: Set<number>) => {
    for (const n of nodes) {
      if (n.figli.length) { acc.add(n.id); collectExpandable(n.figli, acc); }
    }
  };

  const divisioneKey = (d: DivisioneBilancio) => (d.id == null ? 'orphans' : `d-${d.id}`);

  const load = async () => {
    if (!societaId) { setData(null); setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('societaId', societaId);
      params.set('anno', String(anno));
      if (dataDal) params.set('dataDal', dataDal);
      if (dataAl) params.set('dataAl', dataAl);
      // @api GET /api/bilancio-conto-analitico?societaId=&anno=&dataDal=&dataAl= -> { success: true, data: { anno, dataDal, dataAl, periodDays, yearDays, divisioni, alberoRicavi, alberoCosti, totali } }
      const res = await apiFetch<{ success: boolean; data: BilancioData }>(
        `/api/bilancio-conto-analitico?${params}`,
      );
      setData(res.data);
      const exp = new Set<number>();
      collectExpandable(res.data.alberoRicavi, exp);
      collectExpandable(res.data.alberoCosti, exp);
      setExpanded(exp);
      // Per la vista per divisioni, di default tutte chiuse (riepilogo)
      setExpandedDivisioni(new Set());
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [societaId, anno, dataDal, dataAl]);

  const toggle = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleDivisione = (k: string) => {
    setExpandedDivisioni(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const espandiTutto = () => {
    if (!data) return;
    const exp = new Set<number>();
    collectExpandable(data.alberoRicavi, exp);
    collectExpandable(data.alberoCosti, exp);
    setExpanded(exp);
    setExpandedDivisioni(new Set(data.divisioni.map(divisioneKey)));
  };
  const comprimiTutto = () => {
    setExpanded(new Set());
    setExpandedDivisioni(new Set());
  };

  // Deep-link ai movimenti del conto, pre-filtrati con il periodo corrente.
  const movimentiUrl = (n: BilNode): string => {
    const p = new URLSearchParams();
    if (societaId) p.set('societaId', societaId);
    p.set('contoAnaliticoId', String(n.id));
    if (dataDal) p.set('dataDal', dataDal);
    if (dataAl) p.set('dataAl', dataAl);
    return `/controllo-gestione/movimenti-contabilita-analitica?${p}`;
  };

  const renderScostCells = (consuntivo: number, budget: number, natura: Natura) => {
    const scost = consuntivo - budget;
    const cls = classeScost(consuntivo, budget, natura);
    return (
      <>
        <td className={`bop-text-right ${cls}`}>{formatCurrency(scost)}</td>
        <td className={`bop-text-right ${cls}`}>{fmtPerc(consuntivo, budget)}</td>
      </>
    );
  };

  const renderRows = (nodes: BilNode[]): React.ReactNode[] => {
    const out: React.ReactNode[] = [];
    for (const n of nodes) {
      if (hideZero && isZero(n.consuntivo) && isZero(n.budget)) continue;
      const isLeaf = n.figli.length === 0;
      const isOpen = expanded.has(n.id);
      out.push(
        <tr key={n.id} className={isLeaf ? '' : 'bca-row--parent'}>
          <td>
            <div className={`bca-codice-cell bca-codice-cell--liv${n.livello}`}>
              {!isLeaf ? (
                <button
                  type="button"
                  className={`bca-toggle ${isOpen ? 'bca-toggle--open' : ''}`}
                  onClick={() => toggle(n.id)}
                  aria-label={isOpen ? 'Comprimi' : 'Espandi'}
                >
                  <RowIcon.Chevron />
                </button>
              ) : (
                <span className="bca-toggle-spacer" />
              )}
              <span className="bca-codice">{n.codice}</span>
            </div>
          </td>
          <td>
            {isLeaf ? (
              <a
                className="bca-sottoconto-link"
                href={movimentiUrl(n)}
                target="_blank"
                rel="noopener noreferrer"
                title="Apri i movimenti di questo conto nel periodo selezionato"
              >
                {n.descrizione}
              </a>
            ) : (
              n.descrizione
            )}{' '}
            <span className="bca-livello-tag">{LIVELLO_LABEL[n.livello]}</span>
          </td>
          <td className="bop-text-right">{formatCurrency(n.consuntivo)}</td>
          <td className="bop-text-right">{formatCurrency(n.budget)}</td>
          {renderScostCells(n.consuntivo, n.budget, n.natura)}
        </tr>,
      );
      if (!isLeaf && isOpen) out.push(...renderRows(n.figli));
    }
    return out;
  };

  // Render della singola Divisione: riepilogo + drill-down su richiesta
  const renderDivisione = (d: DivisioneBilancio): React.ReactNode[] => {
    const t = d.totali;
    const k = divisioneKey(d);
    const isOpen = expandedDivisioni.has(k);
    const out: React.ReactNode[] = [];

    if (hideZero && isZero(t.ricaviConsuntivo) && isZero(t.ricaviBudget) && isZero(t.costiConsuntivo) && isZero(t.costiBudget)) {
      return out;
    }

    // Riga header divisione con totali (clic per espandere)
    out.push(
      <tr key={`${k}-h`} className="bca-divisione-row">
        <td>
          <div className="bca-codice-cell">
            <button
              type="button"
              className={`bca-toggle ${isOpen ? 'bca-toggle--open' : ''}`}
              onClick={() => toggleDivisione(k)}
              aria-label={isOpen ? 'Comprimi divisione' : 'Espandi divisione'}
            >
              <RowIcon.Chevron />
            </button>
            <span className="bca-codice bca-codice--divisione">{d.codice}</span>
          </div>
        </td>
        <td>
          <strong>{d.descrizione}</strong>{' '}
          <span className="bca-livello-tag bca-livello-tag--divisione">Divisione</span>
        </td>
        <td className="bop-text-right">{formatCurrency(t.risultatoConsuntivo)}</td>
        <td className="bop-text-right">{formatCurrency(t.risultatoBudget)}</td>
        {renderScostCells(t.risultatoConsuntivo, t.risultatoBudget, 'RICAVO')}
      </tr>,
    );

    if (isOpen) {
      // RICAVI sezione
      out.push(<tr key={`${k}-r-h`} className="bca-section-row bca-section-row--nested"><td colSpan={6}>RICAVI</td></tr>);
      const ricaviRows = renderRows(d.alberoRicavi);
      if (ricaviRows.length === 0) {
        out.push(<tr key={`${k}-r-e`} className="bca-row--empty"><td colSpan={6}><span className="bop-text-muted">Nessun ricavo nel periodo.</span></td></tr>);
      } else {
        out.push(...ricaviRows);
      }
      out.push(
        <tr key={`${k}-r-t`} className="bca-subtotal-row">
          <td colSpan={2}>Totale Ricavi {d.codice}</td>
          <td className="bop-text-right">{formatCurrency(t.ricaviConsuntivo)}</td>
          <td className="bop-text-right">{formatCurrency(t.ricaviBudget)}</td>
          {renderScostCells(t.ricaviConsuntivo, t.ricaviBudget, 'RICAVO')}
        </tr>,
      );

      // COSTI sezione
      out.push(<tr key={`${k}-c-h`} className="bca-section-row bca-section-row--nested"><td colSpan={6}>COSTI</td></tr>);
      const costiRows = renderRows(d.alberoCosti);
      if (costiRows.length === 0) {
        out.push(<tr key={`${k}-c-e`} className="bca-row--empty"><td colSpan={6}><span className="bop-text-muted">Nessun costo nel periodo.</span></td></tr>);
      } else {
        out.push(...costiRows);
      }
      out.push(
        <tr key={`${k}-c-t`} className="bca-subtotal-row">
          <td colSpan={2}>Totale Costi {d.codice}</td>
          <td className="bop-text-right">{formatCurrency(t.costiConsuntivo)}</td>
          <td className="bop-text-right">{formatCurrency(t.costiBudget)}</td>
          {renderScostCells(t.costiConsuntivo, t.costiBudget, 'COSTO')}
        </tr>,
      );

      // Margine divisione
      out.push(
        <tr key={`${k}-m`} className="bca-result-row bca-result-row--divisione">
          <td colSpan={2}>Margine Divisione {d.codice}</td>
          <td className="bop-text-right">{formatCurrency(t.risultatoConsuntivo)}</td>
          <td className="bop-text-right">{formatCurrency(t.risultatoBudget)}</td>
          {renderScostCells(t.risultatoConsuntivo, t.risultatoBudget, 'RICAVO')}
        </tr>,
      );
    }

    return out;
  };

  const noSocieta = !societaId;
  const t = data?.totali;

  // Divisioni filtrate per il dropdown
  const divisioniDaMostrare = data?.divisioni.filter(d => {
    if (filtroDivisione && String(d.id ?? 'orphans') !== filtroDivisione) return false;
    return true;
  }) ?? [];

  return (
    <Layout>
      <div className="bilancio-contab-analitica">
        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2>Bilancio contabilità analitica</h2>
            <p>
              Consuntivo a competenza vs budget rapportato al periodo
              {data ? ` — ${fmtData(data.dataDal)} → ${fmtData(data.dataAl)} (${data.periodDays}/${data.yearDays} gg)` : ''}
            </p>
          </div>
          <button
            className="bop-btn bop-btn-secondary"
            onClick={() => setStampaOpen(true)}
            disabled={noSocieta || !data}
            title="Stampa / PDF del bilancio del periodo"
          >
            Stampa
          </button>
        </div>

        <div className="bop-filter-bar">
          {!isUnica && (
            <select className="bop-select" value={societaId} onChange={e => setSocietaId(e.target.value)}>
              <option value="">Seleziona società…</option>
              {listaSocieta.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          )}
          <label className="bca-filter-field">
            <span className="bca-filter-label">Esercizio</span>
            <select className="bop-select" value={anno} onChange={e => cambiaAnno(Number(e.target.value))}>
              {ANNI.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </label>
          <label className="bca-filter-field">
            <span className="bca-filter-label">Dal</span>
            <input className="bop-input" type="date" value={dataDal} min={`${anno}-01-01`} max={`${anno}-12-31`} onChange={e => setDataDal(e.target.value)} />
          </label>
          <label className="bca-filter-field">
            <span className="bca-filter-label">Al</span>
            <input className="bop-input" type="date" value={dataAl} min={`${anno}-01-01`} max={`${anno}-12-31`} onChange={e => setDataAl(e.target.value)} />
          </label>
          <label className="bca-filter-field">
            <span className="bca-filter-label">Vista</span>
            <select className="bop-select" value={vista} onChange={e => setVista(e.target.value as Vista)}>
              <option value="divisioni">Per Divisione</option>
              <option value="natura">Per Natura</option>
            </select>
          </label>
          {vista === 'divisioni' && data && data.divisioni.length > 0 && (
            <label className="bca-filter-field">
              <span className="bca-filter-label">Divisione</span>
              <select className="bop-select" value={filtroDivisione} onChange={e => setFiltroDivisione(e.target.value)}>
                <option value="">Tutte</option>
                {data.divisioni.map(d => (
                  <option key={divisioneKey(d)} value={String(d.id ?? 'orphans')}>
                    {d.codice} — {d.descrizione}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="bop-flex">
            <input type="checkbox" checked={hideZero} onChange={e => setHideZero(e.target.checked)} />
            <span>Nascondi righe a zero</span>
          </label>
          {!noSocieta && data && (
            <div className="bca-spacer">
              <button className="bop-btn bop-btn-secondary" onClick={espandiTutto}>Espandi tutto</button>
              <button className="bop-btn bop-btn-secondary" onClick={comprimiTutto}>Comprimi tutto</button>
            </div>
          )}
        </div>

        {error && <div className="bop-inline-error">{error}</div>}

        <div className="bop-card">
          <div className="bop-table-wrapper">
            {noSocieta ? (
              <div className="bop-empty-state">Seleziona una società per vedere il bilancio.</div>
            ) : loading ? (
              <div className="bop-empty-state">Caricamento…</div>
            ) : !data ? (
              <div className="bop-empty-state">Nessun dato.</div>
            ) : vista === 'divisioni' ? (
              <table className="bop-table bca-table">
                <thead>
                  <tr>
                    <th>Codice</th>
                    <th>Descrizione</th>
                    <th className="bop-text-right">Consuntivo</th>
                    <th className="bop-text-right">Budget</th>
                    <th className="bop-text-right">Scostam. €</th>
                    <th className="bop-text-right">Scostam. %</th>
                  </tr>
                </thead>
                <tbody>
                  {divisioniDaMostrare.length === 0 ? (
                    <tr><td colSpan={6}><div className="bop-empty-state">Nessuna divisione da mostrare.</div></td></tr>
                  ) : (
                    divisioniDaMostrare.map(d => renderDivisione(d))
                  )}
                  {t && !filtroDivisione && (
                    <tr className="bca-result-row">
                      <td colSpan={2}>TOTALE GENERALE (Ricavi − Costi)</td>
                      <td className="bop-text-right">{formatCurrency(t.risultatoConsuntivo)}</td>
                      <td className="bop-text-right">{formatCurrency(t.risultatoBudget)}</td>
                      {renderScostCells(t.risultatoConsuntivo, t.risultatoBudget, 'RICAVO')}
                    </tr>
                  )}
                </tbody>
              </table>
            ) : (
              <table className="bop-table bca-table">
                <thead>
                  <tr>
                    <th>Codice</th>
                    <th>Descrizione</th>
                    <th className="bop-text-right">Consuntivo</th>
                    <th className="bop-text-right">Budget</th>
                    <th className="bop-text-right">Scostam. €</th>
                    <th className="bop-text-right">Scostam. %</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bca-section-row"><td colSpan={6}>RICAVI</td></tr>
                  {renderRows(data.alberoRicavi)}
                  {t && (
                    <tr className="bca-subtotal-row">
                      <td colSpan={2}>Totale Ricavi</td>
                      <td className="bop-text-right">{formatCurrency(t.ricaviConsuntivo)}</td>
                      <td className="bop-text-right">{formatCurrency(t.ricaviBudget)}</td>
                      {renderScostCells(t.ricaviConsuntivo, t.ricaviBudget, 'RICAVO')}
                    </tr>
                  )}

                  <tr className="bca-section-row"><td colSpan={6}>COSTI</td></tr>
                  {renderRows(data.alberoCosti)}
                  {t && (
                    <tr className="bca-subtotal-row">
                      <td colSpan={2}>Totale Costi</td>
                      <td className="bop-text-right">{formatCurrency(t.costiConsuntivo)}</td>
                      <td className="bop-text-right">{formatCurrency(t.costiBudget)}</td>
                      {renderScostCells(t.costiConsuntivo, t.costiBudget, 'COSTO')}
                    </tr>
                  )}
                </tbody>
                {t && (
                  <tfoot>
                    <tr className="bca-result-row">
                      <td colSpan={2}>Risultato (Ricavi − Costi)</td>
                      <td className="bop-text-right">{formatCurrency(t.risultatoConsuntivo)}</td>
                      <td className="bop-text-right">{formatCurrency(t.risultatoBudget)}</td>
                      {renderScostCells(t.risultatoConsuntivo, t.risultatoBudget, 'RICAVO')}
                    </tr>
                  </tfoot>
                )}
              </table>
            )}
          </div>
        </div>
      </div>

      {stampaOpen && societaId && (
        <PrintModal
          societaId={Number(societaId)}
          entitaType="bilancio_analitica"
          entitaId={Number(societaId)}
          entitaLabel={`Bilancio contab. analitica ${anno}`}
          extraOpts={{
            anno,
            ...(dataDal ? { dataDal } : {}),
            ...(dataAl ? { dataAl } : {}),
          }}
          onClose={() => setStampaOpen(false)}
        />
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
